import React, { useState, useEffect, useCallback } from 'react';

/**
 * Persistent crisis banner (Stage 4). Polls /api/crisis-events/unresolved
 * every 30s. When there are unresolved events across any managed account
 * the caregiver manages, a red bar pins to the top of the app. Clicking
 * the banner or its Review button calls onReview(event) so App.js can open
 * the Care dashboard on the Alerts tab — resolution now happens there.
 */
export default function CrisisBanner({ token, api, onReview }) {
  const [events, setEvents] = useState([]);

  const fetchUnresolved = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${api}/api/crisis-events/unresolved`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return; // silent — banner just won't show
      const data = await res.json();
      setEvents(data.crisis_events || []);
    } catch {
      /* network blip — try again next poll */
    }
  }, [token, api]);

  useEffect(() => {
    fetchUnresolved();
    const id = setInterval(fetchUnresolved, 30000);
    return () => clearInterval(id);
  }, [fetchUnresolved]);

  if (events.length === 0) return null;

  const top = events[0];
  const when = top.triggered_at ? new Date(top.triggered_at).toLocaleString() : 'recently';
  const review = () => onReview?.(top);

  return (
    <div className="crisis-banner" onClick={review} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') review(); }}>
      <span className="crisis-banner-dot" />
      <span>
        <strong>{top.managed_name || 'Someone you care for'}</strong> needs your
        attention{top.trigger_phrase ? `: ${top.trigger_phrase}` : ''} ({when}).
        {events.length > 1 ? ` +${events.length - 1} more` : ''}
      </span>
      <button className="crisis-banner-review" onClick={(e) => { e.stopPropagation(); review(); }}>Review</button>
    </div>
  );
}
